<template>
	<v-row class="ma-0 pb-offset">
        <v-col cols="12" class="pa-0">		
			<v-card class="round-card elevation-1 background-2">
				<v-card-title>
					<v-icon left>mdi-cog</v-icon>
					<p>Configurazione WhatsApp Business</p>
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<v-form ref="form" v-model="valid" autocomplete="off">
						<!-- Abilitazione WhatsApp -->
						<v-switch
							v-model="settings.active"
							label="Abilita WhatsApp Business"
							class="mb-4"
							inset
						></v-switch>

						<!-- Selezione Provider -->
						<v-radio-group
							v-model="settings.provider"
							:disabled="!settings.active"
							row
							class="mb-4"
						>
							<v-radio
								label="Twilio"
								value="twilio"
							></v-radio>
							<v-radio
								label="Meta WhatsApp Business API"
								value="meta"
							></v-radio>
						</v-radio-group>

						<!-- Configurazione Twilio -->
						<template v-if="settings.provider === 'twilio'">
							<v-subheader class="px-0">Credenziali Twilio</v-subheader>

							<v-row>
								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.twilio_account_sid"
										label="Account SID"
										:rules="settings.provider === 'twilio' ? [rules.required] : []"
										outlined
										dense
										:disabled="!settings.active"
										autocomplete="off"
										name="whatsapp-twilio-account-sid"
									></v-text-field>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.twilio_auth_token"
										label="Auth Token"
										:rules="settings.provider === 'twilio' ? [rules.required] : []"
										:type="showAuthToken ? 'text' : 'password'"
										:append-icon="showAuthToken ? 'mdi-eye' : 'mdi-eye-off'"
										@click:append="showAuthToken = !showAuthToken"
										outlined
										dense
										:disabled="!settings.active"
										autocomplete="new-password"
										name="whatsapp-twilio-auth-token"
									></v-text-field>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.twilio_telephone_number"
										label="Numero WhatsApp Business"
										:rules="settings.provider === 'twilio' ? [rules.required, rules.phone] : []"
										prefix="+"
										outlined
										dense
										:disabled="!settings.active"
										hint="Formato: +39XXXXXXXXXX"
										autocomplete="off"
										name="whatsapp-twilio-phone"
									></v-text-field>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.webhook_url"
										label="Webhook URL"
										outlined
										dense
										readonly
										:disabled="!settings.active"
										append-icon="mdi-content-copy"
										@click:append="copyWebhookUrl"
									></v-text-field>
								</v-col>
							</v-row>
						</template>

						<!-- Configurazione Meta -->
						<template v-if="settings.provider === 'meta'">
							<v-subheader class="px-0">Credenziali Meta WhatsApp Business API</v-subheader>

							<v-row>
								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.meta_business_app_id"
										label="Business App ID"
										:rules="settings.provider === 'meta' ? [rules.required] : []"
										outlined
										dense
										:disabled="!settings.active"
										hint="ID dell'app business di Meta"
										autocomplete="off"
										name="whatsapp-meta-app-id"
									></v-text-field>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.meta_business_app_secret"
										label="Business App Secret"
										:rules="settings.provider === 'meta' ? [rules.required] : []"
										:type="showMetaSecret ? 'text' : 'password'"
										:append-icon="showMetaSecret ? 'mdi-eye' : 'mdi-eye-off'"
										@click:append="showMetaSecret = !showMetaSecret"
										outlined
										dense
										:disabled="!settings.active"
										autocomplete="new-password"
										name="whatsapp-meta-app-secret"
									></v-text-field>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.meta_whatsapp_business_account_id"
										label="WhatsApp Business Account ID"
										:rules="settings.provider === 'meta' ? [rules.required] : []"
										outlined
										dense
										:disabled="!settings.active"
										hint="WABA ID"
										autocomplete="off"
										name="whatsapp-meta-waba-id"
									></v-text-field>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.meta_phone_number_id"
										label="Phone Number ID"
										:rules="settings.provider === 'meta' ? [rules.required] : []"
										outlined
										dense
										:disabled="!settings.active"
										hint="ID del numero di telefono"
										autocomplete="off"
										name="whatsapp-meta-phone-id"
									></v-text-field>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12">
									<v-text-field
										v-model="settings.meta_access_token"
										label="Access Token"
										:rules="settings.provider === 'meta' ? [rules.required] : []"
										:type="showMetaToken ? 'text' : 'password'"
										:append-icon="showMetaToken ? 'mdi-eye' : 'mdi-eye-off'"
										@click:append="showMetaToken = !showMetaToken"
										outlined
										dense
										:disabled="!settings.active"
										hint="Token di accesso permanente o temporaneo"
										autocomplete="new-password"
										name="whatsapp-meta-access-token"
									></v-text-field>
								</v-col>
							</v-row>

							<v-row>
								<v-col cols="12" md="6">
									<v-text-field
										v-model="settings.meta_webhook_verify_token"
										label="Webhook Verify Token"
										:rules="settings.provider === 'meta' ? [rules.required] : []"
										outlined
										dense
										:disabled="!settings.active"
										hint="Token di verifica per il webhook"
										autocomplete="off"
										name="whatsapp-meta-webhook-token"
									></v-text-field>
								</v-col>

								<v-col cols="12" md="6">
									<v-text-field
										v-model="metaWebhookUrl"
										label="Meta Webhook URL"
										outlined
										dense
										readonly
										:disabled="!settings.active"
										append-icon="mdi-content-copy"
										@click:append="copyMetaWebhookUrl"
									></v-text-field>
								</v-col>
							</v-row>
						</template>


						<!-- Opzioni Avanzate -->
						<v-divider class="my-4"></v-divider>
						<v-subheader class="px-0">Opzioni Avanzate</v-subheader>

						<v-row>
							<v-col cols="12" md="6">
								<v-autocomplete
									v-model="settings.default_country_code"
									:items="countryCodes"
									item-text="label"
									item-value="code"
									label="Prefisso Telefonico Predefinito"
									outlined
									dense
									clearable
									hint="Prefisso utilizzato di default per nuove conversazioni"
									persistent-hint
								>
									<template v-slot:item="{ item }">
										<v-list-item-content>
											<v-list-item-title>{{ item.flag }} {{ item.country }}</v-list-item-title>
											<v-list-item-subtitle>{{ item.code }}</v-list-item-subtitle>
										</v-list-item-content>
									</template>
									<template v-slot:selection="{ item }">
										{{ item.flag }} {{ item.code }} ({{ item.country }})
									</template>
								</v-autocomplete>
							</v-col>

							<!-- Assegnazione Automatica -->
							<v-col cols="12">
								<v-divider class="my-4"></v-divider>
								<v-subheader class="px-0 text-h6">Assegnazione Automatica Conversazioni</v-subheader>
							</v-col>

							<v-col cols="12">
								<v-switch
									v-model="settings.auto_assign_enabled"
									label="Abilita assegnazione automatica delle conversazioni"
									inset
									hint="Assegna automaticamente le nuove conversazioni agli utenti"
									persistent-hint
								></v-switch>
							</v-col>

							<template v-if="settings.auto_assign_enabled">
								<v-col cols="12">
									<v-subheader class="px-0 mt-2">Quando viene trovato un contatto o azienda</v-subheader>
									<v-radio-group
										v-model="settings.assign_strategy_with_entity"
										mandatory
										dense
									>
										<v-radio
											label="Responsabile (Owner) dell'entità"
											value="owner"
										></v-radio>
										<v-radio
											label="Creatore (Creator) dell'entità"
											value="creator"
										></v-radio>
										<v-radio
											label="Utente collegato all'agente dell'azienda"
											value="agent_accessor"
										></v-radio>
										<v-radio
											label="Utente specifico"
											value="specific_user"
										></v-radio>
									</v-radio-group>
								</v-col>

								<v-col cols="12" v-if="settings.assign_strategy_with_entity === 'specific_user'">
									<v-autocomplete
										v-model="settings.assign_user_id_with_entity"
										:items="$store.getters.usersSeq"
										item-text="value"
										item-value="id"
										label="Seleziona utente"
										outlined
										dense
										clearable
										prepend-icon="mdi-account"
										:rules="[v => !!v || 'Seleziona un utente']"
									></v-autocomplete>
								</v-col>

								<v-col cols="12">
									<v-subheader class="px-0 mt-2">Quando NON viene trovato un contatto o azienda</v-subheader>
									<v-autocomplete
										v-model="settings.assign_user_id_without_entity"
										:items="$store.getters.usersSeq"
										item-text="value"
										item-value="id"
										label="Utente di default (obbligatorio)"
										outlined
										dense
										prepend-icon="mdi-account-arrow-right"
										hint="Questo utente gestirà tutte le conversazioni senza entità associata"
										persistent-hint
										:rules="[v => !!v || 'Campo obbligatorio']"
									></v-autocomplete>
								</v-col>

								<v-col cols="12">
									<v-subheader class="px-0 mt-2">Creatore Conversazioni Automatiche</v-subheader>
									<v-autocomplete
										v-model="settings.auto_create_user_id"
										:items="$store.getters.usersSeq"
										item-text="value"
										item-value="id"
										label="Utente creatore per conversazioni auto-generate"
										outlined
										dense
										prepend-icon="mdi-account-plus"
										hint="Utente che verrà impostato come creatore quando una conversazione viene creata automaticamente da un messaggio in entrata"
										persistent-hint
										clearable
									></v-autocomplete>
								</v-col>

								<v-col cols="12">
									<v-subheader class="px-0 mt-2">Gestione Notifiche</v-subheader>
								</v-col>
								<v-col cols="12" md="6">
									<v-text-field
										v-model.number="settings.notification_throttle_minutes"
										label="Intervallo minimo tra notifiche (minuti)"
										type="number"
										outlined
										dense
										suffix="minuti"
										prepend-icon="mdi-clock-outline"
										hint="Per evitare notifiche multiple ravvicinate per la stessa conversazione"
										persistent-hint
										:rules="[v => v > 0 || 'Deve essere maggiore di 0']"
									></v-text-field>
								</v-col>
							</template>

							<v-col cols="12">
								<v-divider class="my-4"></v-divider>
							</v-col>

							<v-col cols="12">
								<v-switch
									v-model="settings.enable_notifications"
									label="Abilita notifiche per nuovi messaggi"
									inset
									hide-details
								></v-switch>
							</v-col>

							<v-col cols="12">
								<v-switch
									v-model="settings.save_media_attachments"
									label="Salva allegati multimediali"
									inset
									hide-details
								></v-switch>
							</v-col>
						</v-row>
					</v-form>
				</v-card-text>
				<v-divider></v-divider>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn
						color="primary"
						@click="testConnection"
						:loading="testing"
						:disabled="!settings.active || !valid"
					>
						<v-icon left>mdi-connection</v-icon>
						Test Connessione {{ settings.provider === 'meta' ? 'Meta' : 'Twilio' }}
					</v-btn>
					<v-btn
						color="success"
						@click="saveSettings"
						:loading="saving"
						:disabled="!valid"
					>
						<v-icon left>mdi-content-save</v-icon>
						Salva Impostazioni
					</v-btn>
				</v-card-actions>

				<v-alert
					v-if="testResult"
					:type="testResult.success ? 'success' : 'error'"
					dismissible
					class="mx-0 mb-4"
				>
					{{ testResult.message }}
				</v-alert>
			</v-card>
		</v-col>
	</v-row>
</template>

<script>
import Vue from 'vue'
import { countryCodes } from '@/utils/countryCodes'

export default {
	name: 'WhatsAppOptions',

	data() {
		return {
			settings: {
				active: false,
				provider: 'twilio', // 'twilio' or 'meta'
				// Twilio settings
				twilio_account_sid: '',
				twilio_auth_token: '',
				twilio_telephone_number: '',
				// Meta settings
				meta_business_app_id: '',
				meta_business_app_secret: '',
				meta_whatsapp_business_account_id: '',
				meta_phone_number_id: '',
				meta_access_token: '',
				meta_webhook_verify_token: '',
				// Common settings
				webhook_url: '',
				default_country_code: '+39',
				auto_assign_enabled: false,
				assign_strategy_with_entity: 'owner',
				assign_user_id_with_entity: null,
				assign_user_id_without_entity: null,
				auto_create_user_id: null,
				notification_throttle_minutes: 60,
				enable_notifications: true,
				save_media_attachments: true
			},
			metaWebhookUrl: '',
			showAuthToken: false,
			showMetaToken: false,
			showMetaSecret: false,
			valid: false,
			saving: false,
			testing: false,
			testResult: null,
			countryCodes,
			rules: {
				required: v => !!v || 'Campo obbligatorio',
				email: v => !v || /.+@.+\..+/.test(v) || 'Email non valida',
				phone: v => !v || /^\+?[0-9]{10,15}$/.test(v) || 'Numero non valido',
				positive: v => v > 0 || 'Deve essere maggiore di zero',
				percentage: v => (v >= 0 && v <= 100) || 'Deve essere tra 0 e 100'
			}
		}
	},

	mounted() {
		this.loadSettings()
	},

	methods: {
		async loadSettings() {
			try {
				const response = await this.axios.get('/whatsapp-settings')
				this.settings = { ...this.settings, ...response.data.data }

				// Generate webhook URLs
				this.settings.webhook_url = `${window.location.origin}/api/whatsapp/webhook`
				this.metaWebhookUrl = `${window.location.origin}/api/whatsapp/meta-webhook`
				this.$store.commit('endProgress')
			} catch (error) {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore nel caricamento delle impostazioni',
					value: true,
					color: 'error'
				})
				this.$store.commit('endProgress')
			}
		},

		async saveSettings() {
			if (!this.$refs.form.validate()) {
				return
			}

			this.saving = true
			try {
				await this.axios.post('/whatsapp-settings', this.settings)
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Impostazioni salvate con successo',
					value: true,
					color: 'success'
				})
			} catch (error) {
				this.$store.commit('setSnackbar', {
					timeout: 6000,
					text: 'Errore nel salvataggio delle impostazioni',
					value: true,
					color: 'error'
				})
			} finally {
				this.saving = false
			}
		},

		async testConnection() {
			this.testing = true
			this.testResult = null

			try {
				let response
				if (this.settings.provider === 'meta') {
					response = await this.axios.post('/whatsapp-test-meta-connection', {
						business_app_id: this.settings.meta_business_app_id,
						business_app_secret: this.settings.meta_business_app_secret,
						whatsapp_business_account_id: this.settings.meta_whatsapp_business_account_id,
						phone_number_id: this.settings.meta_phone_number_id,
						access_token: this.settings.meta_access_token
					})
				} else {
					response = await this.axios.post('/whatsapp-test-connection', {
						account_sid: this.settings.twilio_account_sid,
						auth_token: this.settings.twilio_auth_token,
						telephone_number: this.settings.twilio_telephone_number
					})
				}

				this.testResult = {
					success: true,
					message: response.data.message || 'Connessione riuscita!'
				}
			} catch (error) {
				this.testResult = {
					success: false,
					message: error.response?.data?.message || 'Connessione fallita'
				}
			} finally {
				this.testing = false
			}
		},

		copyWebhookUrl() {
			navigator.clipboard.writeText(this.settings.webhook_url)
			this.$store.commit('setSnackbar', {
				timeout: 6000,
				text: 'URL copiato negli appunti',
				value: true,
				color: 'success'
			})
		},

		copyMetaWebhookUrl() {
			navigator.clipboard.writeText(this.metaWebhookUrl)
			this.$store.commit('setSnackbar', {
				timeout: 6000,
				text: 'Meta Webhook URL copiato negli appunti',
				value: true,
				color: 'success'
			})
		}
	},

	computed: {
		isProviderMeta() {
			return this.settings.provider === 'meta'
		},
		isProviderTwilio() {
			return this.settings.provider === 'twilio'
		}
	}
}
</script>